import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import { useManagementGetAddressesByEntity } from '@apps-common/apis/management/address'
import { StepDirection } from '@apps-common/apps/portal/pages/orders/pages/create/components/management'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputSM, SelectInputSM, TextAreaSM } from '@apps-common/components/elements/form'
import { CollapsibleContent, VertSpacerLg } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { EntityAddressCreateModal } from '@apps-common/components/modals/EntityAddressCreateModal'
import { commonIcons } from '@apps-common/config/icons'
import { CreateOrderSteps } from '@apps-common/config/orders'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { useManagementOrderSelectItems } from '@apps-common/hooks/useManagementOrderSelectItems'
import { IEntityAddressModel } from '@apps-common/types/management/entity-address'

interface IProps {
  isLoading: boolean
  isUpdating: boolean
  step: CreateOrderSteps
  move: (direction: StepDirection) => void
}

export const ManagementOrderDetailsForm: React.FC<IProps> = ({ isLoading, isUpdating, step, move }) => {
  const { register, setValue, watch, formState } = useFormContext()
  const { isSubmitting, errors } = formState
  const { isLoading: selectItemsIsLoading, companies, contacts } = useManagementOrderSelectItems()

  const companyIdValue = watch('companyId')
  const contactIdValue = watch('contactId')
  const pickUpValue = watch('pickUp')

  const {
    data: addresses,
    isLoading: addressesIsLoading,
    isError: addressesIsError,
    refetch: addressesRefetch
  } = useManagementGetAddressesByEntity({
    config: {
      enabled: contactIdValue?.length > 0
    },
    entityId: contactIdValue
  })

  useEffect(() => {
    setValue('addressId', '')
  }, [contactIdValue])

  useEffect(() => {
    const cy = companies?.find((c) => c.companyId === companyIdValue)

    if (cy && cy.defaultMonitoringCentreId) {
      setValue('mCentreId', cy.defaultMonitoringCentreId, { shouldValidate: true })
    }
  }, [companyIdValue])

  useEffect(() => {
    if (pickUpValue) {
      setValue('addressId', '', { shouldValidate: true })
    }
  }, [pickUpValue])

  const loading = isLoading || selectItemsIsLoading
  const addressesLoading = addressesIsLoading || addressesIsError
  const updating = isSubmitting || isUpdating || step !== CreateOrderSteps.details

  return (
    <CollapsibleContent
      key='order-create-details-form'
      title='Step 1 | Details'
      defaultOpen={step === CreateOrderSteps.details}
      disabled={step !== CreateOrderSteps.details}
      classes='bg-surface border border-border rounded-2xl'
    >
      <div className='grid sm:grid-cols-2 gap-x-[15px]'>
        <SelectInputSM
          key='companyId'
          type='text'
          placeholder='Bureau placing order'
          label='Bureau'
          id='companyId'
          showLabel
          loading={loading}
          disabled={updating}
          registration={register(`companyId`, {
            required: FORM_DEFAULT_REQUIRED
          })}
          options={[...companies.map((c) => ({ label: c.companyName, value: c.companyId }))]}
          errors={errors?.companyId as any}
        />
        <SelectInputSM
          key='mCentreId'
          type='text'
          placeholder='Company Monitoring Centre'
          label='Monitoring Centre'
          id='mCentreId'
          showLabel
          loading={loading}
          disabled={updating}
          registration={register(`mCentreId`, {
            required: FORM_DEFAULT_REQUIRED
          })}
          options={[
            { label: 'Select Monitoring Centre', value: '' },
            ...companies.map((c) => ({ label: c.companyName, value: c.companyId }))
          ]}
          errors={errors?.mCentreId as any}
        />
      </div>
      <SelectInputSM
        key='contactId'
        type='text'
        placeholder='Contact placing order'
        label='Contact'
        id='contactId'
        showLabel
        loading={loading}
        disabled={updating}
        registration={register(`contactId`, {
          required: FORM_DEFAULT_REQUIRED
        })}
        options={[
          { label: 'Select Contact', value: '' },
          ...contacts.map((c) => ({ label: `${c.name} - ${c.companyName}`, value: c.contactId }))
        ]}
        errors={errors?.contactId as any}
      />
      <CheckboxInputSM
        label='Order is to be picked up'
        id='pickUp'
        containerClassName='mt-4'
        loading={loading}
        disabled={updating}
        registration={register('pickUp')}
        errors={errors.pickUp as any}
      />
      <div className='flex items-start justify-between gap-[10px]'>
        <SelectInputSM
          key='addressId'
          type='text'
          placeholder='Select delivery address'
          label='Address'
          id='addressId'
          showLabel
          containerClassName='grow mt-[10px]'
          loading={loading}
          disabled={updating || addressesLoading || pickUpValue || !contactIdValue || contactIdValue?.length === 0}
          registration={register(`addressId`, {
            validate: (value) => {
              if (!pickUpValue && (!value || value.length === 0)) {
                return 'Required'
              }

              return true
            }
          })}
          options={[...(addresses?.map((a) => ({ label: `${a.streetAddress1} - ${a.city}`, value: a.addressId })) || [])]}
          errors={errors?.addressId as any}
        />
        <ModalButton
          component={EntityAddressCreateModal}
          disabled={loading || updating || pickUpValue || !contactIdValue || contactIdValue?.length === 0}
          componentProps={{ entityId: contactIdValue }}
          confirmCallback={async (address: IEntityAddressModel) => {
            await addressesRefetch()
            setTimeout(() => {
              setValue('addressId', address.addressId, { shouldValidate: true })
            }, 250)
          }}
        >
          <ButtonRoundSm type='div' className='mt-[37px] action-brand-solid'>
            <commonIcons.add />
          </ButtonRoundSm>
        </ModalButton>
      </div>
      <InputSM
        type='text'
        placeholder='Order number'
        label='Order number'
        id='orderNo'
        showLabel
        loading={loading}
        disabled={updating}
        registration={register('orderNo', {
          required: FORM_DEFAULT_REQUIRED
        })}
        errors={errors.orderNo as any}
      />
      <TextAreaSM
        type='text'
        placeholder='Enter any instructions for this order'
        label='Instructions'
        id='orderNo'
        showLabel
        loading={loading}
        disabled={updating}
        registration={register('instructions')}
        errors={errors.instructions as any}
      />
      <div className='flex items-center justify-end'>
        <ButtonRoundedSm
          type='submit'
          loading={loading || updating}
          text='Next'
          icon={<commonIcons.arrowRight />}
          iconRight
          className='sm:grow-0 action-success'
        />
      </div>
      <VertSpacerLg />
    </CollapsibleContent>
  )
}
